import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ImageGenerateResponse, ImageGenerationService } from '../../core/services/image-generation';

@Component({
  selector: 'app-image-library',
  imports: [DatePipe],
  templateUrl: './image-library.html',
  styleUrl: './image-library.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageLibraryComponent implements OnInit {
  private api = inject(ImageGenerationService);
  private destroyRef = inject(DestroyRef);

  images = signal<ImageGenerateResponse[]>([]);
  isLoading = signal(false);
  errorMsg = signal<string | null>(null);
  pendingDelete = signal<ImageGenerateResponse | null>(null);
  deleteClosing = signal(false);
  deletingId = signal<string | null>(null);
  private deleteCloseTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      if (this.deleteCloseTimer) clearTimeout(this.deleteCloseTimer);
    });
    this.load();
  }

  askDelete(image: ImageGenerateResponse): void {
    if (image.in_use || this.deletingId()) return;
    if (this.deleteCloseTimer) {
      clearTimeout(this.deleteCloseTimer);
      this.deleteCloseTimer = null;
    }
    this.deleteClosing.set(false);
    this.pendingDelete.set(image);
  }

  closeDelete(): void {
    if (this.deletingId() || !this.pendingDelete() || this.deleteClosing()) return;
    this.deleteClosing.set(true);
    this.deleteCloseTimer = setTimeout(() => {
      this.pendingDelete.set(null);
      this.deleteClosing.set(false);
      this.deleteCloseTimer = null;
    }, 200);
  }

  confirmDelete(): void {
    const image = this.pendingDelete();
    if (!image || image.in_use || this.deletingId()) return;
    this.deletingId.set(image.generation_id);
    this.api.delete(image.generation_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.images.update(list => list.filter(item => item.generation_id !== image.generation_id));
        this.deletingId.set(null);
        this.closeDelete();
      },
      error: (err: HttpErrorResponse) => {
        this.deletingId.set(null);
        if (err.status === 409) {
          this.images.update(list => list.map(item => (
            item.generation_id === image.generation_id ? { ...item, in_use: true } : item
          )));
          this.closeDelete();
          this.errorMsg.set('This image is used by a content and cannot be deleted.');
          return;
        }
        this.errorMsg.set('Could not delete the image.');
      },
    });
  }

  private load(): void {
    this.isLoading.set(true);
    this.api.history(200).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.images.set(list);
        this.isLoading.set(false);
      },
      error: () => {
        this.errorMsg.set('Could not load images.');
        this.isLoading.set(false);
      },
    });
  }
}
